import { Component } from '@angular/core';
import Quill from 'quill';
import { IndentStyle } from 'src/app/shared/utils/quill-indent';

const DEFAULT_FONT_FAMILY = 'tahoma'; // set the same value in the html dropdown
const DEFAULT_FONT_SIZE = '8pt'; // set the same value in the html dropdown
const DEFAULT_FDESC_STYLE = `font-family:${DEFAULT_FONT_FAMILY};font-size:${DEFAULT_FONT_SIZE}`;
const LEGACY_DEFAULT_FDESC_STYLE = `${DEFAULT_FDESC_STYLE};`;
const DEFAULT_FDESC_HTML = `<div style="${DEFAULT_FDESC_STYLE}"><br></div>`;

const normalizeContent = (value: string): string =>
  value.replace(/\s|"/g, '').toLowerCase();

const EMPTY_CONTENT_SIGNATURES = new Set<string>([
  normalizeContent('<p><br></p>'),
  normalizeContent(DEFAULT_FDESC_HTML),
  normalizeContent(`<p style="${LEGACY_DEFAULT_FDESC_STYLE}"><br></p>`),
  normalizeContent(`<div style="${LEGACY_DEFAULT_FDESC_STYLE}"><br></div>`),
  normalizeContent(`<p style="${DEFAULT_FDESC_STYLE}"><br></p>`),
  normalizeContent(`<div style="${DEFAULT_FDESC_STYLE}"><br></div>`)
]);

@Component({
  selector: 'app-fdesc-modal',
  templateUrl: './f-desc.component.html',
  styleUrls: ['./f-desc.component.less'],
  standalone: false
})
export class FurtherDescriptionComponent {
  private _fdescHtml: string = DEFAULT_FDESC_HTML;

  get fdescHtml(): string {
    return this._fdescHtml;
  }

  set fdescHtml(value: string | null) {
    if (this.isEmpty(value)) {
      this._fdescHtml = DEFAULT_FDESC_HTML;
      return;
    }

    this._fdescHtml = value as string;
  }

  /**
   * Returns HTML ready for persistence/export.
   * Ensures base font family/size are explicitly present.
   */
  getPersistedHtml(): string {
    return this.ensureDefaultFontMarkup(this._fdescHtml);
  }

  get wordCount(): number {
    if (this.isEmpty(this._fdescHtml)) return 0;
    return this._fdescHtml.length;
  }

  constructor() {
    const alignClass = Quill.import('attributors/style/align');
    const backgroundClass = Quill.import('attributors/style/background');
    const colorClass = Quill.import('attributors/style/color');
    const directionClass = Quill.import('attributors/style/direction');
    const fontType = Quill.import('attributors/style/font');
    fontType.whitelist = ['tahoma', 'arial', 'roboto', 'monospace', 'cursive'];
    const fontSize = Quill.import('attributors/style/size');
    fontSize.whitelist = [
      '4pt',
      '5pt',
      '6pt',
      '7pt',
      '8pt',
      '9pt',
      '10pt',
      '12pt',
      '14pt',
      '15pt',
      '18pt',
      '24pt',
      '36pt'
    ];
    const block = Quill.import('blots/block');
    block.tagName = 'div';

    Quill.register(alignClass, true);
    Quill.register(backgroundClass, true);
    Quill.register(colorClass, true);
    Quill.register(directionClass, true);
    Quill.register(fontType, true);
    Quill.register(fontSize, true);
    Quill.register(block, true);
    Quill.register(IndentStyle, true);
  }

  onEditorCreated(editor: Quill) {
    const applyDefaultStyles = () => {
      const root = editor.root as HTMLElement;
      root.style.fontFamily = DEFAULT_FONT_FAMILY;
      root.style.fontSize = DEFAULT_FONT_SIZE;
    };

    applyDefaultStyles();
    setTimeout(applyDefaultStyles, 100);
  }

  private isEmpty(value: string | null | undefined): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim().length === 0) return true;
    const normalized = normalizeContent(value);
    return normalized.length === 0 || EMPTY_CONTENT_SIGNATURES.has(normalized);
  }

  private ensureDefaultFontMarkup(html: string): string {
    // Guarantee persisted HTML always contains explicit font information so
    // downstream renderers (e.g. DevExpress reports) can mirror the editor view.
    if (!html) return DEFAULT_FDESC_HTML;
    if (typeof window === 'undefined' || !window.document) return html;

    const container = window.document.createElement('div');
    container.innerHTML = html;

    const elements = container.querySelectorAll<HTMLElement>('div, p, span, li, td, th');

    elements.forEach((element) => {
      const declarations = this.extractStyleDeclarations(element.getAttribute('style'));
      const normalizedProps = declarations.map((prop) => prop.toLowerCase());
      const hasFontFamily = normalizedProps.some((prop) => prop.startsWith('font-family'));
      const hasFontSize = normalizedProps.some((prop) => prop.startsWith('font-size'));

      if (!hasFontFamily) declarations.push(`font-family: ${DEFAULT_FONT_FAMILY}`);
      if (!hasFontSize) declarations.push(`font-size: ${DEFAULT_FONT_SIZE}`);

      element.setAttribute('style', declarations.join('; '));
    });

    return container.innerHTML;
  }

  private extractStyleDeclarations(style: string | null): string[] {
    if (!style) return [];

    return style
      .split(';')
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .map((part) => {
        const [property, ...rest] = part.split(':');
        if (!property || rest.length === 0) return part;
        const value = rest.join(':').trim();
        return `${property.trim()}: ${value}`;
      });
  }
}
